import React from 'react';
import { Card, Row, Col, Statistic, Spin } from 'antd';

/**
 * SummaryMetricsCard - Generic component for displaying metrics in a horizontal card layout
 * 
 * @param {Object} props
 * @param {Array} props.metrics - Array of metric configurations
 *   Each metric object should have:
 *   - key: string - unique identifier
 *   - title: string - display label
 *   - value: number|string - the value to display
 *   - icon: ReactNode - Ant Design icon component
 *   - color: string - hex color for the value and icon
 *   - formatter: function (optional) - custom formatter, defaults to toString
 *   - suffix: string (optional) - suffix to append to value
 *   - prefix: string (optional) - prefix to prepend to value
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {string} props.periodLabel - Optional period label to display at bottom (e.g., "Feb 22 - Mar 01, 2026")
 * @param {Object} props.cardStyle - Optional custom styles for the card
 * @param {Object} props.colSpan - Optional column span config (e.g., { xs: 12, sm: 8, md: 4 })
 */
const SummaryMetricsCard = ({ 
  metrics = [], 
  loading = false, 
  error = null,
  periodLabel = null,
  cardStyle = {},
  colSpan = null
}) => {
  
  // Default formatter just returns the value as-is
  const defaultFormatter = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toString();
  };

  // Calculate responsive column span based on number of metrics
  const getColSpan = (totalMetrics) => {
    if (colSpan) return colSpan;
    
    // Responsive defaults based on metric count
    if (totalMetrics <= 4) {
      return { xs: 12, sm: 12, md: 6 };
    } else if (totalMetrics <= 6) {
      return { xs: 12, sm: 8, md: 4 };
    } else {
      return { xs: 12, sm: 8, md: 3 };
    }
  };

  const responsiveSpan = getColSpan(metrics.length);

  if (error) {
    return (
      <Card 
        style={{ 
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          ...cardStyle
        }}
      >
        <div style={{ textAlign: 'center', color: '#f5222d', padding: '12px' }}>
          Error loading metrics: {error}
        </div>
      </Card>
    );
  }

  return (
    <Card 
      style={{ 
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        ...cardStyle
      }}
      bodyStyle={{ padding: '16px 24px' }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' }}>
          <Spin tip="Loading metrics..." />
        </div>
      ) : (
        <Row gutter={[24, 16]} justify="space-between">
          {metrics.map((metric) => {
            const formatter = metric.formatter || defaultFormatter;
            const formattedValue = formatter(metric.value);
            
            return (
              <Col key={metric.key} {...responsiveSpan}>
                <Statistic
                  title={
                    <span style={{ 
                      color: '#8c8c8c', 
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      {metric.icon && React.cloneElement(metric.icon, { 
                        style: { color: metric.color || '#162349' } 
                      })}
                      {metric.title}
                    </span>
                  }
                  value={formattedValue}
                  prefix={metric.prefix}
                  suffix={metric.suffix}
                  valueStyle={{ 
                    color: metric.color || '#162349', 
                    fontSize: '20px',
                    fontWeight: 600
                  }}
                />
              </Col>
            );
          })}
        </Row>
      )}
      
      {/* Period indicator */}
      {periodLabel && !loading && (
        <div style={{ 
          marginTop: '12px', 
          paddingTop: '12px', 
          borderTop: '1px solid #F4F4F4',
          fontSize: '12px',
          color: '#8c8c8c',
          textAlign: 'right'
        }}>
          Period: {periodLabel}
        </div>
      )}
    </Card>
  );
};

export default SummaryMetricsCard;